import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../../api";
import { buildTree, allTasks, statusOf } from "../workplan/util";
import { todayISO } from "./dates";
import { byKind, nodesOfRun, runProgress, splitRuns, startedLabel } from "./runs";
import TaskEditor from "./TaskEditor";
import WorkplanList from "../workplan/WorkplanList";
import WorkplanBoard from "../workplan/WorkplanBoard";
import WorkplanOwner from "../workplan/WorkplanOwner";

// The Phase 2 workplan's three renderers, unchanged. Its fourth — Timeline —
// is deliberately absent: it lays tasks out over a twelve-week grid, and
// marketing work has no week model, so every span would come back null and the
// view would render an empty frame.
const VIEWS = [
  { key: "list", label: "List", icon: "≣" },
  { key: "board", label: "Kanban", icon: "▤" },
  { key: "owner", label: "By owner", icon: "◫" },
];

// Which owner code is "me". Same assumption canEditTask makes: the cockpit has
// two humans in it, and admin is Marco.
const myCode = (isAdmin) => (isAdmin ? "MH" : "WH");

// The shared renderers read `critical`; marketing stores `priority`, which is
// a thing Marco sets and changes rather than a fixed property of week one.
// Mapping here keeps one source of truth and one word for it.
const withCritical = (nodes) =>
  nodes.map((n) => (n.level === "task" ? { ...n, critical: n.priority === "high" } : n));

// "Sept 20" — the year is noise on a list that is mostly this month, and the
// full ISO date is what the editor shows.
function shortDate(iso) {
  const [y, m, d] = (iso || "").split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined,
    { month: "short", day: "numeric" });
}

// Keep my tasks, and the epics and themes that still have one. Filtering the
// flat list alone would leave empty epics rendering their headers over nothing.
export function onlyMine(nodes, code) {
  const tasks = nodes.filter((n) => n.level === "task" && n.owner === code);
  const keep = new Set(tasks.map((t) => t.id));
  let grew = true;
  while (grew) {
    grew = false;
    for (const n of nodes) {
      if (keep.has(n.id) || n.level === "task") continue;
      if (nodes.some((c) => c.parentId === n.id && keep.has(c.id))) {
        keep.add(n.id);
        grew = true;
      }
    }
  }
  return nodes.filter((n) => keep.has(n.id));
}

// One event's checklist, rendered as its own self-contained tree.
function RunBlock({ theme, nodes, isAdmin, onStatus, onEdit, onDelete, renderMeta, past }) {
  const mine = nodesOfRun(nodes, theme.id);
  // Two taps, not one: a run holds ticks that cannot be rebuilt.
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    setBusy(true);
    try { await onDelete(theme.id); } finally { setBusy(false); setConfirming(false); }
  };
  const { done, total } = runProgress(nodes, theme.id);
  const missed = past ? total - done : 0;
  return (
    <div className={`mk-run ${past ? "past" : "live"}`} data-testid={`mk-run-${theme.id}`}>
      <div className="mk-run-head">
        <span className="mk-run-when">{startedLabel(theme.startedAt)}</span>
        <span className="mk-run-name">{theme.title}</span>
        {theme.captureId && <span className="mk-pill">Capture filed</span>}
        <span className="mk-run-count">{done}/{total} done</span>
        {onEdit && (
          <button type="button" className="wp-edit" onClick={() => onEdit(theme)}
            data-testid={`mk-run-rename-${theme.id}`}>Rename</button>
        )}
        {onDelete && !confirming && (
          <button type="button" className="mk-run-trash" onClick={() => setConfirming(true)}
            aria-label="Delete this event" title="Delete this event"
            data-testid={`mk-run-delete-${theme.id}`}>
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"
              fill="none" stroke="currentColor" strokeWidth="1.8"
              strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 7h16M10 11v6M14 11v6M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12M9 7V4h6v3" />
            </svg>
          </button>
        )}
        {onDelete && confirming && (
          <>
            <button type="button" className="wp-edit danger" disabled={busy}
              onClick={remove} data-testid={`mk-run-delete-confirm-${theme.id}`}>
              {busy ? "Deleting…" : "Delete this event"}
            </button>
            <button type="button" className="wp-edit" disabled={busy}
              onClick={() => setConfirming(false)}>Keep</button>
          </>
        )}
      </div>
      {/* Says what was missed rather than just how much — a finished event with
          the sign-off skipped is the thing worth seeing months later. */}
      {past && missed > 0 && (
        <div className="mk-run-missed" data-testid={`mk-run-missed-${theme.id}`}>
          {missed} {missed === 1 ? "item" : "items"} never ticked
        </div>
      )}
      <WorkplanList
        tree={buildTree(withCritical(mine))} nodes={withCritical(mine)}
        isAdmin={isAdmin} onStatus={onStatus} showDoneWhen renderMeta={renderMeta}
        showThemeHead={false}
      />
    </div>
  );
}

export default function MarketingTasks({ isAdmin }) {
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [view, setView] = useState("list");
  const [mine, setMine] = useState(false);
  const [starting, setStarting] = useState(false);
  // null = the button; a string = the name field is open. A run called
  // "Event" is indistinguishable from the one before it, so it gets named
  // before it exists rather than renamed later (which nobody did).
  const [runName, setRunName] = useState(null);
  const [showPast, setShowPast] = useState(false);
  const [showTemplate, setShowTemplate] = useState(false);
  // The node open in the editor drawer, admin only.
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    return api.get("/marketing/tasks")
      .then(({ data }) => { setNodes(data.items); setError(""); })
      .catch((e) => setError(formatApiErrorDetail(e?.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Optimistic: the tick lands under the finger, and reverts with a reason if
  // the server refuses. Same shape as the Phase 2 workplan's onStatus.
  const onStatus = async (id, status) => {
    const prev = nodes;
    setNodes(nodes.map((n) => (n.id === id ? { ...n, status } : n)));
    try {
      const { data } = await api.patch(`/marketing/tasks/${id}`, { status });
      setNodes((cur) => cur.map((n) => (n.id === id ? { ...n, ...data } : n)));
    } catch (e) {
      setNodes(prev);
      setError(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const code = myCode(isAdmin);

  // Deliberately unguarded, like the Playbook's save: a rejection has to reach
  // TaskEditor, which keeps the drawer open with the draft intact.
  const saveTask = async (id, patch) => {
    const { data } = await api.patch(`/marketing/tasks/${id}`, patch);
    setNodes((cur) => cur.map((n) => (n.id === id ? { ...n, ...data } : n)));
  };

  // A new task starts empty in the epic it was added under, and opens in the
  // editor straight away — a row called "New task" that Marco has to find
  // again later is how a list fills up with placeholders.
  const addTask = async (epic) => {
    setError("");
    try {
      const { data } = await api.post("/marketing/tasks", {
        level: "task", title: "New task", parentId: epic.id, owner: code,
      });
      setNodes((cur) => [...cur, data]);
      setEditing(data);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  // Ward taps this when he walks in. Copies the template into a run for
  // tonight; the template itself is never touched.
  const startRun = async (e) => {
    e?.preventDefault();
    const title = (runName || "").trim();
    if (!title) return;
    setStarting(true);
    setError("");
    try {
      const { data } = await api.post("/marketing/tasks/runs", { title });
      setNodes((cur) => [...cur, ...data.items]);
      setRunName(null);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setStarting(false);
    }
  };

  const deleteTask = async (id) => {
    const { data } = await api.delete(`/marketing/tasks/${id}`);
    const gone = new Set(data.deleted || [id]);
    setNodes((cur) => cur.filter((n) => !gone.has(n.id)));
  };

  // From the event card itself. Guarded, unlike deleteTask: there is no
  // drawer here to catch a refusal, so it lands in the page's error line.
  const deleteRun = async (id) => {
    setError("");
    try {
      await deleteTask(id);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  // Due date and priority, as chips on the row. A date that has passed on a
  // task that is not done is the one thing here that should be loud.
  const today = todayISO();
  const renderMeta = (node) => (
    <>
      {node.priority === "high" && statusOf(node) !== "done" && (
        <span className="mk-prio high" data-testid={`mk-prio-${node.id}`}>High</span>
      )}
      {node.due && (
        <span
          className={`mk-due ${node.due < today && statusOf(node) !== "done" ? "over" : ""}`}
          data-testid={`mk-due-${node.id}`}
        >
          {node.due < today && statusOf(node) !== "done" ? "Overdue " : ""}
          {shortDate(node.due)}
        </span>
      )}
    </>
  );

  const groups = useMemo(() => byKind(nodes), [nodes]);
  const runs = useMemo(() => splitRuns(nodes), [nodes]);

  // The standing list only. Runs render on their own above it, and the
  // template is a master copy rather than work.
  const shown = useMemo(
    () => withCritical(mine ? onlyMine(groups.plan, code) : groups.plan),
    [groups.plan, mine, code]);
  const tree = useMemo(() => buildTree(shown), [shown]);

  // The number that answers "what needs me?" — my tasks, not yet done, across
  // the standing list and any event still open. Never the template: it is a
  // master copy, and counting it would mean the number never reached zero.
  const waiting = useMemo(() => {
    const counted = [
      ...groups.plan,
      ...runs.open.flatMap((theme) => nodesOfRun(nodes, theme.id)),
    ];
    return allTasks(counted)
      .filter((t) => t.owner === code && statusOf(t) !== "done").length;
  }, [groups.plan, runs.open, nodes, code]);

  if (loading) return <div className="disc-empty">Loading…</div>;
  if (!nodes.length) {
    return (
      <div className="disc-empty" data-testid="mk-tasks-empty">
        No tasks yet. They seed automatically on the next backend boot.
      </div>
    );
  }

  return (
    <div className="mk-tasks" data-testid="mk-tasks">
      <div className="mk-tasks-lede" data-testid="mk-tasks-waiting">
        {waiting === 0
          ? "Nothing is waiting on you."
          : `${waiting} ${waiting === 1 ? "thing is" : "things are"} waiting on you.`}
      </div>

      {error && <div className="mk-error" data-testid="mk-tasks-error">{error}</div>}

      <div className="wp-toolbar">
        <div className="wp-seg" data-testid="mk-tasks-view-toggle">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              type="button"
              className={view === v.key ? "on" : ""}
              onClick={() => setView(v.key)}
              data-testid={`mk-tasks-view-${v.key}`}
            >
              <span className="wp-seg-icon" aria-hidden="true">{v.icon}</span>
              {v.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className={`mk-btn-quiet ${mine ? "on" : ""}`}
          aria-pressed={mine}
          onClick={() => setMine((m) => !m)}
          data-testid="mk-tasks-mine"
        >
          {mine ? "Showing mine" : "Only mine"}
        </button>
      </div>

      {view === "list" && (
        <>
          {/* The live checklist sits above the standing list: at an event it
              is the only thing on this screen that matters. */}
          {runs.open.map((theme) => (
            <RunBlock key={theme.id} theme={theme} nodes={nodes} isAdmin={isAdmin}
              onStatus={onStatus} renderMeta={renderMeta}
              onEdit={setEditing} onDelete={deleteRun} />
          ))}

          {runName === null ? (
            <div className="mk-run-start-row">
              <button type="button" className="mk-btn"
                onClick={() => setRunName("")} data-testid="mk-run-start">
                I'm at an event
              </button>
              <span className="mk-hint">
                Opens a fresh checklist for tonight. Ticking it never touches the
                last one.
              </span>
            </div>
          ) : (
            <form className="mk-run-start-row" onSubmit={startRun}
              data-testid="mk-run-name-form">
              <input className="mk-input" autoFocus value={runName}
                onChange={(e) => setRunName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Escape") setRunName(null); }}
                placeholder="Which event? e.g. Miami — Delta kickoff"
                aria-label="Event name" data-testid="mk-run-name" />
              <button type="submit" className="mk-btn"
                disabled={starting || !runName.trim()} data-testid="mk-run-name-start">
                {starting ? "Starting…" : "Start checklist"}
              </button>
              <button type="button" className="mk-btn-quiet" disabled={starting}
                onClick={() => setRunName(null)}>Cancel</button>
            </form>
          )}

          {runs.closed.length > 0 && (
            <div className="mk-past">
              <button type="button" className="mk-btn-quiet"
                aria-expanded={showPast} onClick={() => setShowPast((v) => !v)}
                data-testid="mk-past-toggle">
                {showPast ? "Hide" : "Show"} past events ({runs.closed.length})
              </button>
              {showPast && runs.closed.map((theme) => (
                <RunBlock key={theme.id} theme={theme} nodes={nodes} isAdmin={isAdmin}
                  onStatus={onStatus} renderMeta={renderMeta}
                  onEdit={isAdmin ? setEditing : null}
                  onDelete={isAdmin ? deleteRun : null} past />
              ))}
            </div>
          )}
        </>
      )}

      {view === "list" && (
        <WorkplanList
          tree={tree} nodes={shown} isAdmin={isAdmin} onStatus={onStatus}
          // Marketing has no week grid; `critical` here means the item that
          // unblocks the most other work.
          critLabel="Biggest unblock"
          // The `?` tooltip does not exist on a phone, and a phone at an event
          // is where this list gets read.
          showDoneWhen
          renderMeta={renderMeta}
          // Editing is Marco's; Ward's control over a task is the tick.
          onEdit={isAdmin ? setEditing : null}
          onAddTask={isAdmin ? addTask : null}
        />
      )}
      {/* The master copy, at the bottom and folded away, because it is edited
          about twice a year and read never. */}
      {view === "list" && isAdmin && groups.template.length > 0 && (
        <div className="mk-template">
          <button type="button" className="mk-btn-quiet" aria-expanded={showTemplate}
            onClick={() => setShowTemplate((v) => !v)} data-testid="mk-template-toggle">
            {showTemplate ? "Hide" : "Edit"} the event checklist
          </button>
          {showTemplate && (
            <>
              <div className="mk-hint">
                The master copy. Editing it changes what the next event's
                checklist contains — events already started keep what they had.
              </div>
              <WorkplanList
                tree={buildTree(withCritical(groups.template))}
                nodes={withCritical(groups.template)}
                isAdmin={isAdmin} onStatus={() => {}} showDoneWhen
                renderMeta={renderMeta} onEdit={setEditing} onAddTask={addTask} />
            </>
          )}
        </div>
      )}

      {view === "board" && <WorkplanBoard nodes={shown} isAdmin={isAdmin} onStatus={onStatus} />}
      {view === "owner" && <WorkplanOwner nodes={shown} isAdmin={isAdmin} onStatus={onStatus} />}

      {editing && (
        <TaskEditor
          node={editing}
          onSave={saveTask}
          onDelete={deleteTask}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
